export type QAType = "fixed" | "fixed-variable" | "fixed-conditional" | "variable";

export const QA_LABELS: Record<QAType, string> = {
  "fixed":            "Fixed",
  "fixed-variable":   "Fixed with variables",
  "fixed-conditional":"Fixed Conditional",
  "variable":         "Variable",
};

export const QA_COLORS: Record<QAType, { bg: string; border: string; badge: string }> = {
  "fixed":            { bg: "transparent",              border: "transparent",            badge: "var(--fg-3)" },
  "fixed-variable":   { bg: "rgba(59,130,246,0.08)",    border: "rgba(59,130,246,0.4)",   badge: "#3b82f6" },
  "fixed-conditional":{ bg: "rgba(251,146,60,0.10)",    border: "rgba(251,146,60,0.5)",   badge: "#f97316" },
  "variable":         { bg: "rgba(139,92,246,0.10)",    border: "rgba(139,92,246,0.5)",   badge: "#8b5cf6" },
};

const CONDITIONAL_RE = [
  /conditional\s+section/i,
  /include\s+(this\s+)?if\b/i,
  /omit\s+if\b/i,
  /include\s+only\s+if\b/i,
  /include\s+when\b/i,
];

// Matches [some placeholder text] but not empty brackets
const VARIABLE_PLACEHOLDER_RE = /\[[^\]]{2,}\]/;

function blockquoteQaType(el: Element): QAType {
  const text = el.textContent ?? "";
  return CONDITIONAL_RE.some((re) => re.test(text)) ? "fixed-conditional" : "variable";
}

function hasPlaceholder(el: Element): boolean {
  return VARIABLE_PLACEHOLDER_RE.test(el.textContent ?? "");
}

function annotateNode(el: Element) {
  if (el.tagName === "BLOCKQUOTE") {
    const type = blockquoteQaType(el);
    const { bg, border } = QA_COLORS[type];
    const h = el as HTMLElement;
    h.style.background = bg;
    h.style.borderLeft = `3px solid ${border}`;
    h.style.borderRadius = "2px";
    h.style.paddingLeft = "10px";
    h.style.marginLeft = "-13px";
    h.setAttribute("data-qa", type);
    return;
  }

  const hasBlockquoteChild = el.querySelector("blockquote") !== null;

  if (!hasBlockquoteChild && hasPlaceholder(el)) {
    const h = el as HTMLElement;
    const { bg, border } = QA_COLORS["fixed-variable"];
    h.style.background = bg;
    h.style.borderLeft = `2px solid ${border}`;
    h.style.paddingLeft = "6px";
    h.setAttribute("data-qa", "fixed-variable");
  }

  for (const child of Array.from(el.children)) {
    annotateNode(child);
  }
}

export function applyQAAnnotations(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const child of Array.from(doc.body.children)) {
    annotateNode(child);
  }
  return doc.body.innerHTML;
}

export type QASummary = {
  fixed: number;
  "fixed-variable": number;
  "fixed-conditional": number;
  variable: number;
};

export function computeQASummary(annotatedHtml: string): QASummary {
  const doc = new DOMParser().parseFromString(annotatedHtml, "text/html");
  const summary: QASummary = { fixed: 0, "fixed-variable": 0, "fixed-conditional": 0, variable: 0 };

  function count(el: Element) {
    const qa = el.getAttribute("data-qa") as QAType | null;
    if (qa && qa in summary) {
      summary[qa]++;
    } else if (!el.closest("[data-qa]")) {
      const tag = el.tagName;
      const BLOCK_TAGS = new Set(["H1","H2","H3","H4","H5","H6","P","LI","TD","TH","BLOCKQUOTE","HR"]);
      if (BLOCK_TAGS.has(tag) && (el.textContent ?? "").trim().length > 0) {
        summary.fixed++;
      }
    }
    for (const child of Array.from(el.children)) count(child);
  }

  for (const child of Array.from(doc.body.children)) count(child);
  return summary;
}
